import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, ControllerRenderProps, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Link,
  Checkbox,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { getCountryOptions } from '../translations/getCountryOptions';
import { validateEmail } from '../validations/validations';
import { ValidateSpanishID, SPANISH_ID_TYPES } from '../validations/nifValidation';
import { createCheckoutSession, stripePromise } from '../services/stripe';
import LanguageSwitcher from './LanguageSwitcher';
import { isDemoEnv, allowApiUsers, showPlanStep } from '../utils/environment';
import { palette } from '../constants/palette';

type ApiUsageType = 'ERP' | 'NO_ERP';

type FormValues = {
  userType: 'Person' | 'Company' | 'Agent';
  useApi: boolean;
  apiUsageType: '' | ApiUsageType;
  mailAddress: string;
  companyName: string;
  firstName: string;
  lastName: string;
  nationalId: string;
  taxType: string;
  vatId: string;
  phoneNumber: string;
  web: string;
  addressStreet: string;
  addressNumber: string;
  postalCode: string;
  city: string;
  country: string;
  subscriptionType: string;
  billingPeriod: 'month' | 'year';
  expensesPacks: number | '';
  password: string;
  confirmPassword: string;
  discountCode: string;
  referredByCode: string;
  referralOptIn: boolean;
};

interface SubscriptionPlan {
  planName: string;
  description: string;
  pricePerMonth: number;
  pricePerYear: number;
  expenseSubUsers?: number | null;
  displayName?: string;
}

interface RegistrationStepsProps {
  subscriptionType: string;
  subscriptionPlans?: SubscriptionPlan[];
  period?: 'month' | 'year';
  referredByCode?: string;
  discountCode?: string;
  inviteToken?: string;
}

const FIELD_MAX = {
  mailAddress: 200,
  companyName: 200,
  firstName: 200,
  lastName: 200,
  nationalId: 200,
  taxType: 200,
  vatId: 200,
  phoneNumber: 30,
  web: 200,
  addressStreet: 200,
  addressNumber: 200,
  postalCode: 20,
  city: 200,
  country: 200,
} as const;

// Same cap as the lambda, which is the one that enforces it.
const EXPENSES_PACKS_MAX = 50;

// "Enterprise" only exists in this form: it is sold as Business Advance +
// Contable plus Billivo Expenses packs, so that is the plan the lambda gets.
const ENTERPRISE_PLAN = 'Enterprise';
const ENTERPRISE_BASE_PLAN = 'Business Advance + Acceso al Contable (IVA no incluido)';

const RegistrationSteps: React.FC<RegistrationStepsProps> = ({
  subscriptionType,
  subscriptionPlans,
  period,
  referredByCode,
  discountCode,
  inviteToken,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const isDemo = isDemoEnv;

  const getRealSubscriptionType = (plan: string) => {
    if (plan === 'Gestor') return 'Free';
    if (plan === ENTERPRISE_PLAN) return ENTERPRISE_BASE_PLAN;
    return plan;
  };

  const normalizeNationalId = (value: string) => value.replace(/\s+/g, '').toUpperCase();

  const baseSchema = useMemo(
    () =>
      yup.object({
        userType: yup.string().oneOf(['Person', 'Company', 'Agent']).required(),
        useApi: yup.boolean(),
        apiUsageType: yup
          .mixed<ApiUsageType | ''>()
          .oneOf(['ERP', 'NO_ERP', ''], t('registration.validation.validOption'))
          .when('useApi', {
            is: true,
            then: (schema) =>
              schema.test(
                'apiUsageType-required',
                t('registration.validation.apiUsageTypeRequired'),
                (value) => value === 'ERP' || value === 'NO_ERP',
              ),
            otherwise: (schema) => schema.notRequired(),
          }),
        mailAddress: yup
          .string()
          .required(t('registration.validation.emailRequired'))
          .max(
            FIELD_MAX.mailAddress,
            t('registration.validation.maxLength', { max: FIELD_MAX.mailAddress }),
          )
          .test('email-format', t('registration.validation.invalidEmail'), (value) =>
            value ? validateEmail(value) : false,
          ),
        companyName: yup.string().when('userType', {
          is: (value: string) => value === 'Company' || value === 'Agent',
          then: (s) =>
            s
              .required(t('registration.validation.companyNameRequired'))
              .max(
                FIELD_MAX.companyName,
                t('registration.validation.maxLength', { max: FIELD_MAX.companyName }),
              ),
          otherwise: (s) => s.notRequired(),
        }),
        firstName: yup.string().when('userType', {
          is: 'Person',
          then: (s) =>
            s
              .required(t('registration.validation.firstNameRequired'))
              .max(
                FIELD_MAX.firstName,
                t('registration.validation.maxLength', { max: FIELD_MAX.firstName }),
              ),
          otherwise: (s) => s.notRequired(),
        }),
        lastName: yup.string().when('userType', {
          is: 'Person',
          then: (s) =>
            s
              .required(t('registration.validation.lastNameRequired'))
              .max(
                FIELD_MAX.lastName,
                t('registration.validation.maxLength', { max: FIELD_MAX.lastName }),
              ),
          otherwise: (s) => s.notRequired(),
        }),
        nationalId: yup
          .string()
          .required(t('registration.validation.nationalIdRequired'))
          .max(
            FIELD_MAX.nationalId,
            t('registration.validation.maxLength', { max: FIELD_MAX.nationalId }),
          )
          .test('spanish-id', t('registration.validation.invalidDocument'), function (value) {
            const { userType } = this.parent as { userType: 'Person' | 'Company' | 'Agent' };
            if (!value) return false;
            const normalizedValue = normalizeNationalId(value);
            const { valid, type } = ValidateSpanishID(normalizedValue);
            if (!valid) {
              return this.createError({
                message: t('registration.validation.invalidDocument'),
              });
            }
            if (userType === 'Company' && type !== SPANISH_ID_TYPES.CIF) {
              return this.createError({
                message: t('registration.validation.invalidCif'),
              });
            }
            if (
              userType === 'Person' &&
              type !== SPANISH_ID_TYPES.DNI &&
              type !== SPANISH_ID_TYPES.NIE
            ) {
              return this.createError({
                message: t('registration.validation.invalidNifNie'),
              });
            }
            if (
              userType === 'Agent' &&
              type !== SPANISH_ID_TYPES.DNI &&
              type !== SPANISH_ID_TYPES.NIE &&
              type !== SPANISH_ID_TYPES.CIF
            ) {
              return this.createError({
                message: t('registration.validation.invalidDocument'),
              });
            }
            return true;
          }),
        taxType: yup.string().when('userType', {
          is: (value: string) => value === 'Person' || value === 'Company',
          then: (s) =>
            s
              .required(t('registration.validation.taxTypeRequired'))
              .max(
                FIELD_MAX.taxType,
                t('registration.validation.maxLength', { max: FIELD_MAX.taxType }),
              ),
          otherwise: (s) => s.notRequired(),
        }),
        vatId: yup
          .string()
          .max(FIELD_MAX.vatId, t('registration.validation.maxLength', { max: FIELD_MAX.vatId })),

        phoneNumber: yup
          .string()
          .required(t('registration.validation.phoneRequired'))
          .max(
            FIELD_MAX.phoneNumber,
            t('registration.validation.maxLength', { max: FIELD_MAX.phoneNumber }),
          ),

        web: yup.string().when('userType', {
          is: 'Agent',
          then: (s) =>
            s
              .max(FIELD_MAX.web, t('registration.validation.maxLength', { max: FIELD_MAX.web }))
              .test('valid-web', t('registration.validation.invalidWebsite'), (value) => {
                if (!value) return true;
                try {
                  const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`;
                  new URL(normalized);
                  return true;
                } catch {
                  return false;
                }
              }),
          otherwise: (s) => s.notRequired(),
        }),
        addressStreet: yup
          .string()
          .required(t('registration.validation.addressRequired'))
          .max(
            FIELD_MAX.addressStreet,
            t('registration.validation.maxLength', { max: FIELD_MAX.addressStreet }),
          ),
        addressNumber: yup
          .string()
          .max(
            FIELD_MAX.addressNumber,
            t('registration.validation.maxLength', { max: FIELD_MAX.addressNumber }),
          ),
        postalCode: yup
          .string()
          .required(t('registration.validation.postalCodeRequired'))
          .max(
            FIELD_MAX.postalCode,
            t('registration.validation.maxLength', { max: FIELD_MAX.postalCode }),
          ),
        city: yup
          .string()
          .required(t('registration.validation.cityRequired'))
          .max(FIELD_MAX.city, t('registration.validation.maxLength', { max: FIELD_MAX.city })),
        country: yup
          .string()
          .required(t('registration.validation.countryRequired'))
          .max(
            FIELD_MAX.country,
            t('registration.validation.maxLength', { max: FIELD_MAX.country }),
          ),
        subscriptionType: yup.string().required(),
        billingPeriod: yup
          .string()
          .oneOf(['month', 'year'])
          .when('subscriptionType', {
            is: (val: string) => getRealSubscriptionType(val) !== 'Free',
            then: (s) => s.required(t('registration.validation.billingPeriodRequired')),
            otherwise: (s) => s.notRequired(),
          }),
        // An emptied field means no packs.
        expensesPacks: yup
          .number()
          .transform((value, original) => (original === '' ? 0 : value))
          .integer()
          .min(0)
          .max(
            EXPENSES_PACKS_MAX,
            t('registration.validation.maxValue', { max: EXPENSES_PACKS_MAX }),
          ),
        password: yup
          .string()
          .required(t('registration.validation.passwordRequired'))
          .min(8, t('registration.validation.passwordMin')),
        confirmPassword: yup
          .string()
          .required(t('registration.validation.confirmPasswordRequired'))
          .oneOf([yup.ref('password')], t('registration.validation.passwordsDoNotMatch')),
        discountCode: yup.string(),
        referredByCode: yup.string(),
        referralOptIn: yup.boolean(),
      }),
    [t],
  );

  const steps = useMemo(
    () =>
      isDemo
        ? [
            t('registration.steps.account'),
            t('registration.steps.userData'),
            t('registration.steps.addressAndConfirmation'),
          ]
        : [
            t('registration.steps.planAndAccount'),
            t('registration.steps.userData'),
            t('registration.steps.addressAndConfirmation'),
          ],
    [t, isDemo],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resolver = useMemo(
    () => yupResolver(baseSchema as yup.ObjectSchema<FormValues>),
    [baseSchema],
  );

  const normalizedInitialSubscriptionType = (() => {
    const normalized = subscriptionType?.toLowerCase();

    if (normalized === 'free') {
      return 'Free';
    }
    if (normalized === 'gestor') {
      return 'Gestor';
    }

    if (normalized === 'business') {
      return 'Business (IVA no incluido)';
    }

    if (normalized === 'business-contable') {
      return 'Business + Acceso al Contable (IVA no incluido)';
    }

    if (normalized === 'autonomo') {
      return 'Autónomo (IVA no incluido)';
    }

    if (normalized === 'autonomo-contable') {
      return 'Autónomo + Acceso al Contable (IVA no incluido)';
    }

    return subscriptionType;
  })();

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    resolver,
    defaultValues: {
      userType: 'Person',
      subscriptionType: normalizedInitialSubscriptionType,
      useApi: false,
      apiUsageType: 'NO_ERP',
      taxType: '01',
      billingPeriod: period || 'month',
      expensesPacks: 0,
      referredByCode,
      discountCode,
      password: '',
      confirmPassword: '',
      web: '',
      referralOptIn: false,
    },
    mode: 'onChange',
  });

  const billingPeriod = watch('billingPeriod');
  const userType = watch('userType');
  const selectedPlan = watch('subscriptionType');
  const realSelectedPlan = getRealSubscriptionType(selectedPlan);
  const isGestorPlan = selectedPlan === 'Gestor';
  const canBuyExpensesPacks = selectedPlan === ENTERPRISE_PLAN;
  const expensesPacks = watch('expensesPacks');
  // Seats the base plan already includes, from the same column Billivo Expenses
  // enforces. Falls back to the 10 Business Advance ships with today.
  const expensesBaseUsers =
    subscriptionPlans?.find((plan) => plan.planName === ENTERPRISE_BASE_PLAN)?.expenseSubUsers ??
    10;
  // Same condition the lambda checks before it applies the coupon, so the
  // notice never promises a discount the checkout will not give.
  const inviteDiscountApplies =
    !!inviteToken &&
    billingPeriod === 'year' &&
    realSelectedPlan.toLowerCase().startsWith('business advance') &&
    realSelectedPlan.toLowerCase().includes('contable');
  const useApiSelected = useWatch({ control, name: 'useApi', defaultValue: false });

  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const countryOptions = useMemo(() => getCountryOptions(currentLanguage), [currentLanguage]);

  const availablePlans = useMemo<SubscriptionPlan[]>(() => {
    const basePlans = subscriptionPlans ?? [
      { planName: 'Free', description: '', pricePerMonth: 0, pricePerYear: 0 },
      {
        planName: 'Autónomo (IVA no incluido)',
        description: '',
        pricePerMonth: 0,
        pricePerYear: 0,
      },
      {
        planName: 'Autónomo + Acceso al Contable (IVA no incluido)',
        description: '',
        pricePerMonth: 0,
        pricePerYear: 0,
      },
      {
        planName: 'Business (IVA no incluido)',
        description: '',
        pricePerMonth: 0,
        pricePerYear: 0,
      },
      {
        planName: 'Business + Acceso al Contable (IVA no incluido)',
        description: '',
        pricePerMonth: 0,
        pricePerYear: 0,
      },
    ];

    const alreadyHasGestor = basePlans.some(
      (plan) => (plan.displayName || plan.planName) === 'Gestor',
    );

    const plans = [...basePlans];

    const freePlan = basePlans.find((plan) => plan.planName === 'Free');
    if (!alreadyHasGestor && freePlan) {
      plans.push({ ...freePlan, displayName: 'Gestor' });
    }

    const enterpriseBase = basePlans.find((plan) => plan.planName === ENTERPRISE_BASE_PLAN);
    if (enterpriseBase) {
      plans.push({ ...enterpriseBase, displayName: ENTERPRISE_PLAN });
    }

    return plans;
  }, [subscriptionPlans]);

  const onCaptchaChange = (token: string | null) => {
    setIsCaptchaVerified(!!token);
    setCaptchaToken(token || '');
  };

  useEffect(() => {
    if (isGestorPlan && userType !== 'Agent') {
      setValue('userType', 'Agent', { shouldValidate: true });
    }
  }, [isGestorPlan, userType, setValue]);

  useEffect(() => {
    if (userType !== 'Agent') {
      setValue('referralOptIn', false);
      setValue('web', '');
    }
  }, [userType, setValue]);

  const handleNext = async () => {
    const stepFields: (keyof FormValues)[][] = isDemo
      ? [
          [
            'userType',
            'companyName',
            'firstName',
            'lastName',
            'mailAddress',
            'password',
            'confirmPassword',
          ],
          ['nationalId', 'vatId', 'phoneNumber', 'web', 'taxType'],
          ['addressStreet', 'addressNumber', 'postalCode', 'city', 'country', 'referredByCode'],
        ]
      : [
          [
            'subscriptionType',
            'billingPeriod',
            'expensesPacks',
            'mailAddress',
            'password',
            'confirmPassword',
          ],
          [
            'userType',
            'companyName',
            'firstName',
            'lastName',
            'nationalId',
            'vatId',
            'phoneNumber',
            'web',
            'taxType',
          ],
          [
            'addressStreet',
            'addressNumber',
            'postalCode',
            'city',
            'country',
            'discountCode',
            'referredByCode',
          ],
        ];

    const fieldsToValidate = stepFields[activeStep];
    const valid = await trigger(fieldsToValidate as unknown as ReadonlyArray<keyof FormValues>);
    if (!valid) return;
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const onSubmit = async (data: FormValues) => {
    if (!isCaptchaVerified || !termsAccepted) return;

    const { confirmPassword, ...submitData } = data;
    submitData.nationalId = normalizeNationalId(submitData.nationalId);
    submitData.subscriptionType = getRealSubscriptionType(data.subscriptionType);
    submitData.expensesPacks = canBuyExpensesPacks ? Number(data.expensesPacks) || 0 : 0;

    if (data.userType === 'Person') {
      submitData.companyName = `${data.firstName} ${data.lastName}`.trim();
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setErrorModalOpen(false);

    try {
      if (submitData.subscriptionType === 'Trial') {
        navigate('/registration-success');
        return;
      }

      const response = await createCheckoutSession({ ...submitData, inviteToken }, captchaToken);

      if (isDemo) {
        const { subscription, apiCredentials } = response;
        if (subscription) {
          navigate('/registration-success', {
            state: {
              apiCredentials: apiCredentials || undefined,
            },
          });
          return;
        }
      }

      const { id: sessionId, url, subscription } = response;

      if (subscription) {
        navigate('/registration-success');
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) throw new Error(t('registration.errors.stripeLoadFailed'));

      if (sessionId) {
        const result = await stripe.redirectToCheckout({ sessionId });
        if (!result || result.error) {
          if (url) {
            window.location.assign(url);
            return;
          }
          throw new Error(result?.error?.message || t('registration.errors.noSession'));
        }
        return;
      }

      if (url) {
        window.location.assign(url);
        return;
      }

      throw new Error(t('registration.errors.noSession'));
    } catch (error: unknown) {
      if (typeof error === 'object' && error && 'message' in error) {
        setErrorMessage(
          (error as { message?: string }).message || t('registration.errors.unknown'),
        );
      } else {
        setErrorMessage(t('registration.errors.submitUnknown'));
      }
      setErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const Accent = ({ children }: { children: React.ReactNode }) => (
    <Box
      sx={{
        background: palette.bgAlt,
        color: palette.primaryDarker,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        display: 'inline-block',
        fontWeight: 600,
      }}
    >
      {children}
    </Box>
  );

  const getPlanLabel = (plan: SubscriptionPlan) => {
    const visibleName = plan.displayName || plan.planName;
    let label = visibleName;

    // Enterprise has no fixed price, it depends on the packs chosen.
    if (plan.planName !== 'Trial' && plan.planName !== 'Free' && visibleName !== ENTERPRISE_PLAN) {
      label +=
        billingPeriod === 'year'
          ? ` - ${plan.pricePerYear} € / ${t('registration.billing.yearShort')}`
          : ` - ${plan.pricePerMonth} € / ${t('registration.billing.monthShort')}`;
    }
    return label;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <LanguageSwitcher />

      <Paper
        elevation={4}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          border: `1px solid ${palette.primaryLighter}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700 }}
            >
              {isDemo ? t('registration.titleDemo') : t('registration.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {t('registration.complete')} <Accent>{t('registration.stepsCount')}</Accent>{' '}
              {t('registration.toCreateAccount')}
            </Typography>
          </Box>

          {showPlanStep && (
            <Chip
              label={selectedPlan || t('registration.planFallback')}
              color="primary"
              variant="outlined"
              size="small"
              sx={{
                borderColor: palette.primary,
                alignSelf: { xs: 'center', sm: 'center' },
                mt: { xs: 1, sm: 0 },
                maxWidth: '100%',
                '& .MuiChip-label': { whiteSpace: 'normal' },
              }}
            />
          )}
        </Box>

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {activeStep === 0 && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {inviteToken && (
                <Grid item xs={12}>
                  <Alert severity={inviteDiscountApplies ? 'success' : 'info'}>
                    {inviteDiscountApplies
                      ? t('registration.inviteApplied')
                      : t('registration.inviteRequiresPlan')}
                  </Alert>
                </Grid>
              )}

              {showPlanStep && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: palette.primaryDark }}>
                      {t('registration.planSelection')}
                    </Typography>
                    <Controller
                      name="subscriptionType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label={t('registration.fields.subscriptionPlan')}
                          fullWidth
                          margin="dense"
                          error={!!errors.subscriptionType}
                        >
                          {availablePlans.map((plan) => {
                            const label = getPlanLabel(plan);
                            const value = plan.displayName || plan.planName;

                            return (
                              <MenuItem
                                key={`${plan.planName}-${value}`}
                                value={value}
                                title={label}
                              >
                                {label}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      )}
                    />
                  </Grid>

                  {realSelectedPlan !== 'Free' && realSelectedPlan !== 'Trial' && (
                    <Grid item xs={12}>
                      <Controller
                        name="billingPeriod"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth margin="dense">
                            <InputLabel id="billing-period-label">
                              {t('registration.fields.billingPeriod')}
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="billing-period-label"
                              label={t('registration.fields.billingPeriod')}
                              error={!!errors.billingPeriod}
                            >
                              <MenuItem value="month">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{t('registration.billing.monthly')}</span>
                                  <Chip
                                    label={t('registration.billing.popular')}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </MenuItem>
                              <MenuItem value="year">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{t('registration.billing.yearly')}</span>
                                  <Chip
                                    label={t('registration.billing.save17')}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </MenuItem>
                            </Select>
                            {errors.billingPeriod && (
                              <FormHelperText error>{errors.billingPeriod.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                  )}

                  {canBuyExpensesPacks && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="expensesPacks"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              // Text and not number: no spinner arrows, and the
                              // field can be emptied while typing.
                              onChange={(event) => {
                                const digits = event.target.value.replace(/\D/g, '');
                                field.onChange(digits === '' ? '' : Number(digits));
                              }}
                              label={t('registration.fields.expensesPacks')}
                              fullWidth
                              margin="dense"
                              error={!!errors.expensesPacks}
                              helperText={errors.expensesPacks?.message}
                              inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: palette.primaryDark }}>
                          {t('registration.expensesTotalUsers', {
                            total: expensesBaseUsers + (Number(expensesPacks) || 0) * 10,
                            base: expensesBaseUsers,
                          })}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </>
              )}

              {isDemo && (
                <Grid item xs={12}>
                  <Controller
                    name="userType"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'userType'>;
                    }) => (
                      <FormControl component="fieldset" sx={{ mb: { xs: 0, sm: 0 } }}>
                        <FormLabel component="legend">{t('common.fields.userType')}</FormLabel>
                        <RadioGroup
                          row
                          {...field}
                          value={isGestorPlan ? 'Agent' : field.value}
                          onChange={(event) => {
                            if (isGestorPlan) return;
                            field.onChange(event.target.value);
                          }}
                        >
                          <FormControlLabel
                            value="Person"
                            control={<Radio />}
                            label={t('registration.userTypes.person')}
                            disabled={isGestorPlan}
                          />
                          <FormControlLabel
                            value="Company"
                            control={<Radio />}
                            label={t('registration.userTypes.company')}
                            disabled={isGestorPlan}
                          />
                          <FormControlLabel
                            value="Agent"
                            control={<Radio />}
                            label={t('registration.userTypes.agent')}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {isDemo && (userType === 'Company' || userType === 'Agent') && (
                <Grid item xs={12}>
                  <Controller
                    name="companyName"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'companyName'>;
                    }) => (
                      <TextField
                        {...field}
                        label={
                          userType === 'Agent'
                            ? t('registration.fields.agentName')
                            : t('common.fields.companyName')
                        }
                        type="text"
                        fullWidth
                        margin="dense"
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                        inputProps={{ maxLength: FIELD_MAX.companyName }}
                      />
                    )}
                  />
                </Grid>
              )}

              {isDemo && userType === 'Person' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, 'firstName'>;
                      }) => (
                        <TextField
                          {...field}
                          label={t('common.fields.firstName')}
                          type="text"
                          fullWidth
                          margin="dense"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          inputProps={{ maxLength: FIELD_MAX.firstName }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, 'lastName'>;
                      }) => (
                        <TextField
                          {...field}
                          label={t('common.fields.lastName')}
                          type="text"
                          fullWidth
                          margin="dense"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          inputProps={{ maxLength: FIELD_MAX.lastName }}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Controller
                  name="mailAddress"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'mailAddress'>;
                  }) => (
                    <TextField
                      {...field}
                      label={t('common.fields.email')}
                      type="email"
                      fullWidth
                      margin="dense"
                      error={!!errors.mailAddress}
                      helperText={errors.mailAddress?.message}
                      inputProps={{ maxLength: FIELD_MAX.mailAddress }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }: { field: ControllerRenderProps<FormValues, 'password'> }) => (
                    <TextField
                      {...field}
                      label={t('common.fields.password')}
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      margin="dense"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword
                                  ? t('registration.accessibility.hidePassword')
                                  : t('registration.accessibility.showPassword')
                              }
                              onClick={() => setShowPassword((prev) => !prev)}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'confirmPassword'>;
                  }) => (
                    <TextField
                      {...field}
                      label={t('common.fields.confirmPassword')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      fullWidth
                      margin="dense"
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showConfirmPassword
                                  ? t('registration.accessibility.hideConfirmPassword')
                                  : t('registration.accessibility.showConfirmPassword')
                              }
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              onMouseDown={(event) => event.preventDefault()}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {allowApiUsers && (
                <Grid item xs={12}>
                  <Controller
                    name="useApi"
                    control={control}
                    render={({ field }: { field: ControllerRenderProps<FormValues, 'useApi'> }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(field.value)}
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                        }
                        label={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              component="span"
                              sx={{ color: 'warning.main', fontWeight: 600, mr: 0.5 }}
                            >
                              {t('registration.api.devOnly')}
                            </Typography>
                            {t('registration.api.useApi')}
                          </Box>
                        }
                      />
                    )}
                  />
                </Grid>
              )}

              {allowApiUsers && useApiSelected && (
                <Grid item xs={12}>
                  <Controller
                    name="apiUsageType"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'apiUsageType'>;
                    }) => (
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.apiUsageType}
                        sx={{ mt: { xs: 0, sm: 1 } }}
                      >
                        <InputLabel id="api-usage-type-label" className="form-text">
                          {t('registration.api.numberingManagement')}
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="api-usage-type-label"
                          label={t('registration.api.numberingManagement')}
                          displayEmpty
                          className="form-text"
                        >
                          <MenuItem value="ERP">{t('registration.api.erpOption')}</MenuItem>
                          <MenuItem value="NO_ERP">{t('registration.api.noErpOption')}</MenuItem>
                        </Select>
                        {errors.apiUsageType && (
                          <FormHelperText error>{errors.apiUsageType.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button variant="contained" color="primary" onClick={handleNext}>
                    {t('common.actions.continue')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {!isDemo && (
                <Grid item xs={12}>
                  <Controller
                    name="userType"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'userType'>;
                    }) => (
                      <FormControl component="fieldset" sx={{ mb: 1 }}>
                        <FormLabel component="legend">{t('common.fields.userType')}</FormLabel>
                        <RadioGroup
                          row
                          {...field}
                          value={isGestorPlan ? 'Agent' : field.value}
                          onChange={(event) => {
                            if (isGestorPlan) return;
                            field.onChange(event.target.value);
                          }}
                        >
                          <FormControlLabel
                            value="Person"
                            control={<Radio />}
                            label={t('registration.userTypes.person')}
                            disabled={isGestorPlan}
                          />
                          <FormControlLabel
                            value="Company"
                            control={<Radio />}
                            label={t('registration.userTypes.company')}
                            disabled={isGestorPlan}
                          />
                          <FormControlLabel
                            value="Agent"
                            control={<Radio />}
                            label={t('registration.userTypes.agent')}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {!isDemo && (userType === 'Company' || userType === 'Agent') && (
                <Grid item xs={12}>
                  <Controller
                    name="companyName"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'companyName'>;
                    }) => (
                      <TextField
                        {...field}
                        label={
                          userType === 'Agent'
                            ? t('registration.fields.agentName')
                            : t('common.fields.companyName')
                        }
                        type="text"
                        fullWidth
                        margin="dense"
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                        inputProps={{ maxLength: FIELD_MAX.companyName }}
                      />
                    )}
                  />
                </Grid>
              )}

              {!isDemo && userType === 'Person' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, 'firstName'>;
                      }) => (
                        <TextField
                          {...field}
                          label={t('common.fields.firstName')}
                          type="text"
                          fullWidth
                          margin="dense"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          inputProps={{ maxLength: FIELD_MAX.firstName }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, 'lastName'>;
                      }) => (
                        <TextField
                          {...field}
                          label={t('common.fields.lastName')}
                          type="text"
                          fullWidth
                          margin="dense"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          inputProps={{ maxLength: FIELD_MAX.lastName }}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6}>
                <Controller
                  name="nationalId"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'nationalId'>;
                  }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(event) => {
                        field.onChange(normalizeNationalId(event.target.value));
                      }}
                      label={
                        userType === 'Company'
                          ? t('common.fields.cif')
                          : userType === 'Agent'
                            ? t('registration.fields.agentDocument')
                            : t('common.fields.nifNie')
                      }
                      fullWidth
                      margin="dense"
                      error={!!errors.nationalId}
                      helperText={errors.nationalId?.message}
                      inputProps={{ maxLength: FIELD_MAX.nationalId }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="vatId"
                  control={control}
                  render={({ field }: { field: ControllerRenderProps<FormValues, 'vatId'> }) => (
                    <TextField
                      {...field}
                      label={
                        userType === 'Agent'
                          ? t('registration.fields.agentVatId')
                          : t('common.fields.vatId')
                      }
                      fullWidth
                      margin="dense"
                      error={!!errors.vatId}
                      helperText={errors.vatId?.message}
                      inputProps={{ maxLength: FIELD_MAX.vatId }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'phoneNumber'>;
                  }) => (
                    <TextField
                      {...field}
                      label={
                        userType === 'Agent'
                          ? t('registration.fields.agentPhoneNumber')
                          : t('registration.fields.phoneNumber')
                      }
                      fullWidth
                      margin="dense"
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                      inputProps={{ maxLength: FIELD_MAX.phoneNumber }}
                    />
                  )}
                />
              </Grid>

              {userType === 'Agent' ? (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="web"
                    control={control}
                    render={({ field }: { field: ControllerRenderProps<FormValues, 'web'> }) => (
                      <TextField
                        {...field}
                        label={t('registration.fields.agentWebsite')}
                        fullWidth
                        margin="dense"
                        error={!!errors.web}
                        helperText={errors.web?.message}
                        inputProps={{ maxLength: FIELD_MAX.web }}
                      />
                    )}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="taxType"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'taxType'>;
                    }) => (
                      <TextField
                        {...field}
                        select
                        label={t('registration.fields.taxType')}
                        fullWidth
                        margin="dense"
                        error={!!errors.taxType}
                        helperText={errors.taxType?.message}
                        defaultValue={'01'}
                        SelectProps={{ native: true }}
                      >
                        <option value="01">{t('registration.taxTypes.peninsula')}</option>
                        <option value="02">{t('registration.taxTypes.ceutaMelilla')}</option>
                        <option value="03">{t('registration.taxTypes.canaryIslands')}</option>
                        <option value="05">{t('registration.taxTypes.other')}</option>
                      </TextField>
                    )}
                  />
                </Grid>
              )}
              {userType === 'Agent' && (
                <Grid item xs={12}>
                  <Controller
                    name="referralOptIn"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'referralOptIn'>;
                    }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(field.value)}
                            onChange={(event) => field.onChange(event.target.checked)}
                            color="primary"
                          />
                        }
                        label={t('registration.fields.referralOptIn')}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Button variant="text" color="inherit" onClick={handleBack}>
                    {t('common.actions.back')}
                  </Button>
                  <Button variant="contained" color="primary" onClick={handleNext}>
                    {t('common.actions.continue')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {activeStep === 2 && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="addressStreet"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'addressStreet'>;
                  }) => (
                    <TextField
                      {...field}
                      label={t('registration.fields.addressLine1')}
                      fullWidth
                      margin="dense"
                      error={!!errors.addressStreet}
                      helperText={errors.addressStreet?.message}
                      inputProps={{ maxLength: FIELD_MAX.addressStreet }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="addressNumber"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'addressNumber'>;
                  }) => (
                    <TextField
                      {...field}
                      label={t('registration.fields.addressLine2')}
                      fullWidth
                      margin="dense"
                      error={!!errors.addressNumber}
                      helperText={errors.addressNumber?.message}
                      inputProps={{ maxLength: FIELD_MAX.addressNumber }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="postalCode"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'postalCode'>;
                  }) => (
                    <TextField
                      {...field}
                      label={t('common.fields.postalCode')}
                      fullWidth
                      margin="dense"
                      error={!!errors.postalCode}
                      helperText={errors.postalCode?.message}
                      inputProps={{ maxLength: FIELD_MAX.postalCode }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }: { field: ControllerRenderProps<FormValues, 'city'> }) => (
                    <TextField
                      {...field}
                      label={t('common.fields.city')}
                      fullWidth
                      margin="dense"
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      inputProps={{ maxLength: FIELD_MAX.city }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      key={currentLanguage}
                      options={countryOptions}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      value={countryOptions.find((c) => c.code === field.value) || null}
                      onChange={(_, newValue) => field.onChange(newValue ? newValue.code : '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('common.fields.country')}
                          fullWidth
                          margin="dense"
                          error={!!errors.country}
                          helperText={errors.country?.message}
                          inputProps={{
                            ...params.inputProps,
                            maxLength: FIELD_MAX.country,
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              {showPlanStep && !inviteToken && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="discountCode"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'discountCode'>;
                    }) => (
                      <TextField
                        {...field}
                        label={t('registration.fields.discountCode')}
                        fullWidth
                        margin="dense"
                        error={!!errors.discountCode}
                        helperText={errors.discountCode?.message}
                        inputProps={{ maxLength: 50 }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* An invited signup is attributed to the gestoría that invited it,
                  so there is no referral to type either. */}
              {!inviteToken && (
                <Grid item xs={12} sm={showPlanStep ? 6 : 12}>
                  <Controller
                    name="referredByCode"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'referredByCode'>;
                    }) => (
                      <TextField
                        {...field}
                        label={t('registration.fields.referredByCode')}
                        fullWidth
                        margin="dense"
                        error={!!errors.referredByCode}
                        helperText={errors.referredByCode?.message}
                        inputProps={{ maxLength: 50 }}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    '& > div': {
                      '@media (max-width: 340px)': {
                        transform: 'scale(0.80)',
                        transformOrigin: 'center',
                      },
                    },
                  }}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                    onChange={onCaptchaChange}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted((e.target as HTMLInputElement).checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography>
                        {t('registration.terms.acceptPrefix')}{' '}
                        <Link
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsTermsModalOpen(true);
                          }}
                          sx={{ textDecoration: 'underline' }}
                        >
                          {t('registration.terms.linkText')}
                        </Link>
                      </Typography>
                    }
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Button variant="text" color="inherit" onClick={handleBack}>
                    {t('common.actions.back')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isCaptchaVerified || !termsAccepted || isSubmitting}
                  >
                    {isSubmitting
                      ? t('registration.actions.sending')
                      : t('registration.actions.finishRegistration')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>

        <Dialog
          open={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>{t('registration.terms.title')}</DialogTitle>
          <DialogContent sx={!isMobile ? { height: '400px' } : {}}>
            <Typography variant="body1" paragraph>
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.general.title')}
              </Typography>
              {t('registration.terms.sections.general.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.definitions.title')}
              </Typography>
              {t('registration.terms.sections.definitions.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.scope.title')}
              </Typography>
              {t('registration.terms.sections.scope.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.access.title')}
              </Typography>
              {t('registration.terms.sections.access.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.usage.title')}
              </Typography>
              {t('registration.terms.sections.usage.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.privacy.title')}
              </Typography>
              {t('registration.terms.sections.privacy.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.communications.title')}
              </Typography>
              {t('registration.terms.sections.communications.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.intellectualProperty.title')}
              </Typography>
              {t('registration.terms.sections.intellectualProperty.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.liability.title')}
              </Typography>
              {t('registration.terms.sections.liability.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.thirdPartyLinks.title')}
              </Typography>
              {t('registration.terms.sections.thirdPartyLinks.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.modifications.title')}
              </Typography>
              {t('registration.terms.sections.modifications.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.termination.title')}
              </Typography>
              {t('registration.terms.sections.termination.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.jurisdiction.title')}
              </Typography>
              {t('registration.terms.sections.jurisdiction.body')}
              <br />
              <br />
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {t('registration.terms.sections.contact.title')}
              </Typography>
              {t('registration.terms.sections.contact.body')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTermsModalOpen(false)}>{t('common.actions.close')}</Button>
            <Button
              onClick={() => {
                setTermsAccepted(true);
                setIsTermsModalOpen(false);
              }}
              variant="contained"
            >
              {t('common.actions.accept')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isSubmitting} PaperProps={{ sx: { textAlign: 'center', p: 4 } }}>
          <DialogContent>
            <CircularProgress />
            <DialogContentText sx={{ mt: 2 }}>
              {t('registration.status.sendingInfo')}
            </DialogContentText>
          </DialogContent>
        </Dialog>

        <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
          <DialogTitle>{t('common.errors.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{errorMessage}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorModalOpen(false)} autoFocus>
              {t('common.actions.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default RegistrationSteps;
